import {useCallback} from 'react';

import {
  PAYMENT_LINK_EVENTS,
  PAYMENT_LINK_OUTCOME_EVENTS,
  PAYMENT_LINK_V2_EVENTS,
} from '@/app/main/paymentLink/events/PaymentLinkEvents';
import {PROJECT_TAGS} from '@/constants';
import {PaymentLinkFlows} from '@/dataSource/src/api/payment-link/constants';
import {useTrackingManager} from '@/hooks';
import {IEventBaseDataSchema} from '@/services/segment/models/TrackingManagerEventModel';

import {
  ConfirmationTermsConditionsPayload,
  ICheckoutResultResponseModel,
  IGetCheckoutResultResponse,
  IPaymentLinkInformation,
  IPaymentLinkV2TrackApi,
  IPublicRequestCheckoutUrl,
} from './entities';
import {
  buildPaymentLinkPayload,
  IPaymentLinkTrackData,
} from './utils/buildPaymentLinkPayload';
import {buildPaymentLinkTrackingPayload} from './utils/buildPaymentLinkTrackingPayload';
import {buildPaymentLinkTrackingPayloadWithPaymentOption} from './utils/buildPaymentLinkTrackingPayloadWithPaymentOption';

export const usePaymentLinkTrackers = () => {
  const {trackEvent} = useTrackingManager();

  const getEventDataFromPayloadApi = useCallback(
    (props: IPaymentLinkV2TrackApi): IEventBaseDataSchema => {
      return {
        project_tag: PROJECT_TAGS.PAYMENT_LINK,
        user: {
          id: props.receiver_user_id ?? '',
        },
        Fintech: {
          receiver: {
            user: {
              id: props.receiver_user_id ?? '',
            },
          },
          payment_link: {
            flow: props.flow,
            payment_method: props.payment_method,
            ...(props?.card_type && {card_type: props.card_type}),
          },
          payment: {
            link: buildPaymentLinkPayload({
              id: props.request_money_id,
              type: props.link_type,
              campaignConfiguration: props.campaign_configuration,
              savingsGoalConfiguration: props.savings_goal_configuration,
            }),
          },
          ...(props.amount !== undefined && {
            transaction: {
              amount: props.amount.toString(),
              method: props.payment_method,
            },
          }),
        },
        Request: {
          payment_link: {
            id: props.request_money_id ?? '',
          },
        },
      };
    },
    [],
  );

  const trackApiPaymentLinkGetInformationSuccess = useCallback(() => {
    trackEvent(PAYMENT_LINK_EVENTS.API_PAYMENT_LINK_GET_INFORMATION_SUCCESS, {
      project_tag: PROJECT_TAGS.PAYMENT_LINK,
    });
  }, [trackEvent]);

  const trackApiPaymentLinkGetInformationError = useCallback(
    error => {
      trackEvent(PAYMENT_LINK_EVENTS.API_PAYMENT_LINK_GET_INFORMATION_ERROR, {
        project_tag: PROJECT_TAGS.PAYMENT_LINK,
        error,
      });
    },
    [trackEvent],
  );

  const trackApiPaymentLinkCreatePublicCheckoutLinkSuccess = useCallback(
    (payload: IPublicRequestCheckoutUrl, checkoutUrl?: string) => {
      trackEvent(
        PAYMENT_LINK_EVENTS.API_PAYMENT_LINK_CREATE_PUBLIC_CHECKOUT_LINK_SUCCESS,
        {
          project_tag: PROJECT_TAGS.PAYMENT_LINK,
          Request: {
            payment_link: {
              id: payload.request_money_id,
            },
            checkout_url: checkoutUrl,
          },
        },
      );
    },
    [trackEvent],
  );

  const trackApiPaymentLinkCreatePublicCheckoutLinkError = useCallback(
    error => {
      trackEvent(
        PAYMENT_LINK_EVENTS.API_PAYMENT_LINK_CREATE_PUBLIC_CHECKOUT_LINK_ERROR,
        {project_tag: PROJECT_TAGS.PAYMENT_LINK, error},
      );
    },
    [trackEvent],
  );

  const trackViewRequestMoneyPaymentRequest = useCallback(
    (response: IPaymentLinkInformation, fundraise?: IPaymentLinkTrackData) => {
      const payload = buildPaymentLinkTrackingPayload(response);
      trackEvent(PAYMENT_LINK_EVENTS.VIEW_REQUEST_MONEY_PAYMENT_REQUEST, {
        ...payload,
        Fintech: {
          ...payload.Fintech,
          payment: {
            link: buildPaymentLinkPayload({
              id: response?.id,
              ...fundraise,
            }),
          },
        },
      });
    },
    [trackEvent],
  );

  const trackApiCheckoutFeesSuccess = useCallback(() => {
    trackEvent(PAYMENT_LINK_EVENTS.API_CHECKOUT_FEES, {
      project_tag: PROJECT_TAGS.PAYMENT_LINK,
    });
  }, [trackEvent]);

  const trackViewRequestMoneyInvalidLink = useCallback(
    (response: IPaymentLinkInformation) => {
      trackEvent(
        PAYMENT_LINK_EVENTS.VIEW_REQUEST_MONEY_INVALID_LINK,
        buildPaymentLinkTrackingPayload(response),
      );
    },
    [trackEvent],
  );
  const trackViewRequestMoneyLinkExpired = useCallback(
    (response: IPaymentLinkInformation) => {
      trackEvent(
        PAYMENT_LINK_EVENTS.VIEW_REQUEST_MONEY_LINK_EXPIRED,
        buildPaymentLinkTrackingPayload(response),
      );
    },
    [trackEvent],
  );

  const trackViewRequestMoneyPaymentOptions = useCallback(
    (
      paymentInfo: IPaymentLinkInformation,
      amount: number,
      prefill: boolean,
    ) => {
      const payload = buildPaymentLinkTrackingPayloadWithPaymentOption(
        paymentInfo,
        amount,
        undefined,
        undefined,
        prefill,
      );
      trackEvent(
        PAYMENT_LINK_EVENTS.VIEW_REQUEST_MONEY_PAYMENT_OPTIONS,
        payload,
      );
    },
    [trackEvent],
  );

  const trackSelectRequestMoneyPaymentOption = useCallback(
    (payload: IEventBaseDataSchema) => {
      trackEvent(
        PAYMENT_LINK_EVENTS.SELECT_REQUEST_MONEY_PAYMENT_OPTION,
        payload,
      );
    },
    [trackEvent],
  );

  const trackViewRequestMoneyPaymentConfirmation = useCallback(() => {
    trackEvent(PAYMENT_LINK_EVENTS.VIEW_REQUEST_MONEY_PAYMENT_CONFIRMATION, {
      project_tag: PROJECT_TAGS.PAYMENT_LINK,
    });
  }, [trackEvent]);

  const trackSelectRequestMoneyPaymentConfirmationTermsConditions = useCallback(
    (payload: ConfirmationTermsConditionsPayload) => {
      trackEvent(
        PAYMENT_LINK_EVENTS.SELECT_REQUEST_MONEY_PAYMENT_CONFIRMATION_TERMS_CONDITIONS,
        {
          project_tag: PROJECT_TAGS.PAYMENT_LINK,
          ...(payload?.receiver?.id && {user: {id: payload?.receiver?.id}}),
          Fintech: {
            receiver: {
              user: {
                id: payload?.receiver?.id,
              },
            },
            transaction: {
              amount: payload.optionSelected.amount,
              fee: payload.optionSelected.fee,
              method: payload.optionSelected.type,
              prefill: payload.optionSelected.prefill,
            },
          },
          Banking: {
            receiver: {
              user: {
                id: payload?.receiver?.id,
              },
            },
            transaction: {
              amount: payload.optionSelected.amount,
              fee: payload.optionSelected.fee,
              method: payload.optionSelected.type,
              prefill: payload.optionSelected.prefill,
            },
          },
          Request: {
            payment_link: {
              id: payload.id,
            },
            description: payload.information.note,
            gif: payload.information.gif,
            flow: payload.single_payment
              ? PaymentLinkFlows.LINK
              : PaymentLinkFlows.QR,
          },
        },
      );
    },
    [trackEvent],
  );

  const trackSelectRequestMoneyPaymentInfoConfirm = useCallback(
    (payload: IEventBaseDataSchema) => {
      trackEvent(
        PAYMENT_LINK_EVENTS.SELECT_REQUEST_MONEY_PAYMENT_INFO_CONFIRM,
        payload,
      );
    },
    [trackEvent],
  );

  const trackSelectRequestMoneyJoinModak = useCallback(
    (screenName?: string, fundraise?: IPaymentLinkTrackData) => {
      trackEvent(PAYMENT_LINK_EVENTS.SELECT_REQUEST_MONEY_JOIN_MODAK, {
        project_tag: PROJECT_TAGS.PAYMENT_LINK,
        Fintech: {
          payment: {
            link: buildPaymentLinkPayload(fundraise),
          },
        },
        ...(screenName && {Web: {screen: {name: screenName}}}),
      });
    },
    [trackEvent],
  );

  const trackSelectGetHelpOnRequestMoneyPaymentError = useCallback(() => {
    trackEvent(
      PAYMENT_LINK_EVENTS.SELECT_GET_HELP_ON_REQUEST_MONEY_PAYMENT_ERROR,
      {project_tag: PROJECT_TAGS.PAYMENT_LINK},
    );
  }, [trackEvent]);
  const trackApiRequestMoneyPaymentOutcomeSuccess = useCallback(
    (transactionId: string, response: IGetCheckoutResultResponse) => {
      trackEvent(
        PAYMENT_LINK_OUTCOME_EVENTS.API_REQUEST_MONEY_PAYMENT_OUTCOME_SUCCESS,
        {
          project_tag: PROJECT_TAGS.PAYMENT_LINK,
          ...(response?.receiver_user?.id && {
            user: {id: response?.receiver_user?.id},
          }),
          Fintech: {
            transaction: {
              id: transactionId ?? '',
              amount: response?.amount?.toString() ?? '',
              fee:
                (response?.amount && response?.amount_without_fee
                  ? response?.amount - response?.amount_without_fee
                  : ''
                ).toString() ?? '',
              method: response?.type ?? '',
            },
            receiver: {
              user: {
                id: response?.receiver_user?.id ?? '',
              },
            },
          },
          Banking: {
            transaction: {
              id: transactionId ?? '',
              amount: response?.amount?.toString() ?? '',
              fee:
                (response?.amount && response?.amount_without_fee
                  ? response?.amount - response?.amount_without_fee
                  : ''
                ).toString() ?? '',
              method: response?.type ?? '',
            },
            receiver: {
              user: {
                id: response?.receiver_user?.id ?? '',
              },
            },
          },
        },
      );
    },
    [trackEvent],
  );

  const trackViewPaymentOutcomeSuccess = useCallback(
    (transactionId: string, response: ICheckoutResultResponseModel) => {
      trackEvent(
        PAYMENT_LINK_OUTCOME_EVENTS.VIEW_REQUEST_MONEY_PAYMENT_OUTCOME_SUCCESS,
        {
          project_tag: PROJECT_TAGS.PAYMENT_LINK,
          ...(response?.receiver_user?.id && {
            user: {id: response?.receiver_user?.id},
          }),
          Fintech: {
            transaction: {
              id: transactionId ?? '',
              amount: response?.amount?.toString() ?? '',
              fee:
                (response?.amount && response?.amount_without_fee
                  ? response?.amount - response?.amount_without_fee
                  : ''
                ).toString() ?? '',
              method: response?.type ?? '',
            },
            receiver: {
              user: {
                id: response?.receiver_user?.id ?? '',
              },
            },
          },
          Banking: {
            transaction: {
              id: transactionId ?? '',
              amount: response?.amount?.toString() ?? '',
              fee:
                (response?.amount && response?.amount_without_fee
                  ? response?.amount - response?.amount_without_fee
                  : ''
                ).toString() ?? '',
              method: response?.type ?? '',
            },
            receiver: {
              user: {
                id: response?.receiver_user?.id ?? '',
              },
            },
          },
        },
      );
    },
    [trackEvent],
  );

  const trackApiRequestMoneyPaymentError = useCallback(
    (error, transactionId: string) => {
      trackEvent(PAYMENT_LINK_OUTCOME_EVENTS.API_REQUEST_MONEY_PAYMENT_ERROR, {
        project_tag: PROJECT_TAGS.PAYMENT_LINK,
        error,
        Fintech: {
          transaction: {
            id: transactionId ?? '',
          },
        },
        Banking: {
          transaction: {
            id: transactionId ?? '',
          },
        },
      });
    },
    [trackEvent],
  );

  const trackSelectChangeAmountPaymentLink = useCallback(
    (
      receiver_user_id: string | undefined,
      request_money_id: string | undefined,
    ) => {
      trackEvent(PAYMENT_LINK_EVENTS.SELECT_PAYMENT_LINK_CUSTOM_AMOUNT, {
        project_tag: PROJECT_TAGS.PAYMENT_LINK,
        ...(receiver_user_id && {
          user: {id: receiver_user_id},
        }),
        Fintech: {
          receiver: {
            user: {
              id: receiver_user_id ?? '',
            },
          },
        },
        Banking: {
          receiver: {
            user: {
              id: receiver_user_id ?? '',
            },
          },
        },
        Request: {
          payment_link: {
            id: request_money_id ?? '',
          },
        },
      });
    },
    [trackEvent],
  );

  const trackViewRequestMoneySuccessScreen = useCallback(
    (params: IPaymentLinkV2TrackApi) => {
      const {
        receiver_user_id,
        request_money_id,
        flow,
        payment_method,
        transaction_id,
        paymentSessionId,
        sessionId,
        paymentId,
        card_type,
      } = params;
      trackEvent(PAYMENT_LINK_V2_EVENTS.view_request_money_success_screen, {
        project_tag: PROJECT_TAGS.PAYMENT_LINK,
        user: {
          id: receiver_user_id ?? '',
        },
        Fintech: {
          transaction: {
            id: transaction_id ?? '',
            payment_session_id: paymentSessionId,
            session_id: sessionId,
            payment_id: paymentId,
          },
          receiver: {
            user: {
              id: receiver_user_id ?? '',
            },
          },
          payment_link: {
            flow,
            payment_method,
            ...(card_type && {card_type}),
          },
        },
        Request: {
          payment_link: {
            id: request_money_id ?? '',
          },
        },
      });
    },
    [trackEvent],
  );

  const trackViewRequestMoneyErrorScreen = useCallback(
    (params: IPaymentLinkV2TrackApi) => {
      const {
        receiver_user_id,
        request_money_id,
        flow,
        payment_method,
        transaction_id,
        paymentSessionId,
        sessionId,
        paymentId,
        card_type,
      } = params;
      trackEvent(PAYMENT_LINK_V2_EVENTS.view_request_money_error_screen, {
        project_tag: PROJECT_TAGS.PAYMENT_LINK,
        user: {
          id: receiver_user_id ?? '',
        },
        Fintech: {
          transaction: {
            id: transaction_id ?? '',
            payment_session_id: paymentSessionId,
            session_id: sessionId,
            payment_id: paymentId,
          },
          receiver: {
            user: {
              id: receiver_user_id ?? '',
            },
          },
          payment_link: {
            flow,
            payment_method,
            ...(card_type && {card_type}),
          },
        },
        Request: {
          payment_link: {
            id: request_money_id ?? '',
          },
        },
      });
    },
    [trackEvent],
  );

  const trackApiRequestMoneyCreateSessionSuccess = useCallback(
    (props: IPaymentLinkV2TrackApi) => {
      trackEvent(
        PAYMENT_LINK_V2_EVENTS.api_request_money_create_session_success,
        getEventDataFromPayloadApi(props),
      );
    },
    [getEventDataFromPayloadApi, trackEvent],
  );

  const trackApiRequestMoneyCreateSessionError = useCallback(
    (props: IPaymentLinkV2TrackApi, error: any) => {
      trackEvent(
        PAYMENT_LINK_V2_EVENTS.api_request_money_create_session_error,
        getEventDataFromPayloadApi(props),
        error,
      );
    },
    [getEventDataFromPayloadApi, trackEvent],
  );

  const trackApiRequestMoneyPaidSuccess = useCallback(
    (props: IPaymentLinkV2TrackApi) => {
      trackEvent(
        PAYMENT_LINK_V2_EVENTS.api_request_money_paid_success,
        getEventDataFromPayloadApi(props),
      );
    },
    [getEventDataFromPayloadApi, trackEvent],
  );

  const trackApiRequestMoneyPaidError = useCallback(
    (props: IPaymentLinkV2TrackApi, error: any) => {
      trackEvent(
        PAYMENT_LINK_V2_EVENTS.api_request_money_paid_error,
        getEventDataFromPayloadApi(props),
        error,
      );
    },
    [getEventDataFromPayloadApi, trackEvent],
  );

  const trackViewRequestMoneyPersonalInfo = useCallback(
    (props: IPaymentLinkV2TrackApi) => {
      trackEvent(
        PAYMENT_LINK_V2_EVENTS.view_request_money_personal_info,
        getEventDataFromPayloadApi(props),
      );
    },
    [getEventDataFromPayloadApi, trackEvent],
  );

  const trackSelectRequestMoneyPersonalInfoContinue = useCallback(
    (props: IPaymentLinkV2TrackApi) => {
      trackEvent(
        PAYMENT_LINK_V2_EVENTS.select_request_money_personal_info_continue,
        getEventDataFromPayloadApi(props),
      );
    },
    [getEventDataFromPayloadApi, trackEvent],
  );

  const trackViewRequestMoneyPaymentInfo = useCallback(
    (props: IPaymentLinkV2TrackApi) => {
      trackEvent(
        PAYMENT_LINK_V2_EVENTS.view_request_money_payment_info,
        getEventDataFromPayloadApi(props),
      );
    },
    [getEventDataFromPayloadApi, trackEvent],
  );

  const trackSelectRequestMoneyShareInitiative = useCallback(
    (fundraise?: IPaymentLinkTrackData) => {
      trackEvent(PAYMENT_LINK_V2_EVENTS.select_request_money_share_initiative, {
        project_tag: PROJECT_TAGS.PAYMENT_LINK,
        Fintech: {
          payment: {
            link: buildPaymentLinkPayload(fundraise),
          },
        },
      });
    },
    [trackEvent],
  );

  const trackCheckoutWebComponentOnReady = useCallback(
    (props: IPaymentLinkV2TrackApi) => {
      trackEvent(
        PAYMENT_LINK_V2_EVENTS.checkout_web_component_on_ready,
        getEventDataFromPayloadApi(props),
      );
    },
    [getEventDataFromPayloadApi, trackEvent],
  );

  const trackCheckoutWebComponentOnSubmit = useCallback(
    (props: IPaymentLinkV2TrackApi) => {
      trackEvent(
        PAYMENT_LINK_V2_EVENTS.checkout_web_component_on_submit,
        getEventDataFromPayloadApi(props),
      );
    },
    [getEventDataFromPayloadApi, trackEvent],
  );

  const trackCheckoutWebComponentOnClick = useCallback(
    (props: IPaymentLinkV2TrackApi) => {
      trackEvent(
        PAYMENT_LINK_V2_EVENTS.checkout_web_component_on_click,
        getEventDataFromPayloadApi(props),
      );
    },
    [getEventDataFromPayloadApi, trackEvent],
  );

  const trackCheckoutWebComponentOnTokenized = useCallback(
    (props: IPaymentLinkV2TrackApi) => {
      trackEvent(
        PAYMENT_LINK_V2_EVENTS.checkout_web_component_on_tokenized,
        getEventDataFromPayloadApi(props),
      );
    },
    [getEventDataFromPayloadApi, trackEvent],
  );

  const trackCheckoutWebComponentOnAuthorized = useCallback(
    (props: IPaymentLinkV2TrackApi) => {
      trackEvent(
        PAYMENT_LINK_V2_EVENTS.checkout_web_component_on_authorized,
        getEventDataFromPayloadApi(props),
      );
    },
    [getEventDataFromPayloadApi, trackEvent],
  );

  return {
    trackApiPaymentLinkGetInformationSuccess,
    trackApiPaymentLinkGetInformationError,
    trackApiPaymentLinkCreatePublicCheckoutLinkSuccess,
    trackApiPaymentLinkCreatePublicCheckoutLinkError,
    trackViewRequestMoneyPaymentRequest,
    trackViewRequestMoneyInvalidLink,
    trackViewRequestMoneyLinkExpired,
    trackViewRequestMoneyPaymentOptions,
    trackSelectRequestMoneyPaymentOption,
    trackViewRequestMoneyPaymentConfirmation,
    trackSelectRequestMoneyPaymentInfoConfirm,
    trackSelectRequestMoneyJoinModak,
    trackSelectGetHelpOnRequestMoneyPaymentError,
    trackApiCheckoutFeesSuccess,
    trackApiRequestMoneyPaymentOutcomeSuccess,
    trackApiRequestMoneyPaymentError,
    trackSelectRequestMoneyPaymentConfirmationTermsConditions,
    trackViewPaymentOutcomeSuccess,
    trackSelectChangeAmountPaymentLink,
    trackViewRequestMoneySuccessScreen,
    trackViewRequestMoneyErrorScreen,
    trackApiRequestMoneyCreateSessionSuccess,
    trackApiRequestMoneyCreateSessionError,
    trackApiRequestMoneyPaidSuccess,
    trackApiRequestMoneyPaidError,
    trackViewRequestMoneyPersonalInfo,
    trackSelectRequestMoneyPersonalInfoContinue,
    trackViewRequestMoneyPaymentInfo,
    trackSelectRequestMoneyShareInitiative,
    trackCheckoutWebComponentOnReady,
    trackCheckoutWebComponentOnSubmit,
    trackCheckoutWebComponentOnClick,
    trackCheckoutWebComponentOnTokenized,
    trackCheckoutWebComponentOnAuthorized,
  };
};
